import { useEffect, useState } from 'react';
import { cn } from '@/utils/cn';
import { DashboardHeader } from '../../organisms/DashboardHeader';
import { useStoryBottomBar } from '@/context/StoryBottomBarContext';
import { HeaderGradientProvider, useHeaderGradient } from '@/context/HeaderGradientContext';
import styles from './StudentDashboardLayout.module.scss';
import type { StudentDashboardLayoutProps } from './StudentDashboardLayout.types';

function StudentDashboardLayoutInner({
  userName,
  userInitials,
  xp,
  notificationCount,
  onNotificationClick,
  onCloseClick,
  compactButtonVariant,
  onSavedItemsClick,
  onProfileClick,
  hideSidebarOnMobile: _hideSidebarOnMobile,
  compactSuppressBottomNavPadding,
  compactCloseOnDesktop,
  onlyBackHeader,
  showInviteDrawerItem,
  onInviteClick,
  onRewardsClick,
  onXpClick,
  onLanguageClick,
  onHelpClick,
  onSettingsClick,
  onSupportClick,
  onLevelTestClick,
  onLogout,
  onDropdownOpen,
  children,
  className,
}: StudentDashboardLayoutProps) {
  const { content: bottomBarContent, headerProgress, hideCloseButton } =
    useStoryBottomBar() ?? { content: null, headerProgress: null, hideCloseButton: false };
  const { extendGradient, backdropGradientOpacity } =
    useHeaderGradient() ?? { extendGradient: false, backdropGradientOpacity: undefined };
  const suppressBottomPadding =
    compactSuppressBottomNavPadding ?? _hideSidebarOnMobile;

  // Degradado inferior (mismo efecto que StoryLayout/karaoke): el contenido se
  // funde tras el botón del footer mientras hay scroll, y desaparece al llegar
  // abajo del todo para que la última card quede totalmente legible. Solo aplica
  // cuando hay barra inferior (Perfil/Ajustes/onboarding).
  const [bottomFadeOpacity, setBottomFadeOpacity] = useState(0);
  useEffect(() => {
    if (!bottomBarContent) {
      setBottomFadeOpacity(0);
      return;
    }
    const FADE_BAND_PX = 140;
    const compute = () => {
      const el = document.scrollingElement ?? document.documentElement;
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      const opacity =
        distanceFromBottom <= 4
          ? 0
          : distanceFromBottom >= FADE_BAND_PX
            ? 1
            : distanceFromBottom / FADE_BAND_PX;
      setBottomFadeOpacity(opacity);
    };
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        compute();
      });
    };
    compute();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [bottomBarContent]);

  return (
    <div className={cn(styles.layout, className)}>
      <div className={styles.layout__main}>
        <DashboardHeader
          extendGradient={extendGradient}
          backdropGradientOpacity={backdropGradientOpacity}
          progress={headerProgress ?? undefined}
          userName={userName}
          userInitials={userInitials}
          xp={xp}
          notificationCount={notificationCount}
          onNotificationClick={onNotificationClick}
          onCloseClick={hideCloseButton ? undefined : onCloseClick}
          compactOnMobile={_hideSidebarOnMobile}
          compactCloseOnDesktop={compactCloseOnDesktop}
          onlyBackHeader={onlyBackHeader}
          compactButtonVariant={compactButtonVariant}
          showInviteDrawerItem={showInviteDrawerItem}
          onInviteClick={onInviteClick}
          onRewardsClick={onRewardsClick}
          onXpClick={onXpClick}
          onLanguageClick={onLanguageClick}
          onHelpClick={onHelpClick}
          onSavedItemsClick={onSavedItemsClick}
          onProfileClick={onProfileClick}
          onSettingsClick={onSettingsClick}
          onSupportClick={onSupportClick}
          onLevelTestClick={onLevelTestClick}
          onLogout={onLogout}
          onDropdownOpen={onDropdownOpen}

        />

        <main
          className={cn(
            styles.layout__content,
            _hideSidebarOnMobile && styles['layout__content--compactHeaderTop'],
            suppressBottomPadding && styles['layout__content--compactMobileBottom']
          )}
          data-has-bottom-bar={bottomBarContent ? '' : undefined}
        >
          {children}
        </main>

        {/* Degradado inferior: funde el contenido tras el botón (como karaoke) */}
        {bottomBarContent && (
          <div
            className={styles.layout__bodyFade}
            style={{ opacity: bottomFadeOpacity }}
            aria-hidden="true"
          />
        )}

        {/* Barra fija abajo (historias) — mismo nivel que el header */}
        {bottomBarContent && (
          <div className={styles.layout__bottomBar}>
            <div className={styles.layout__bottomBarInner}>{bottomBarContent}</div>
          </div>
        )}
      </div>
    </div>
  );
}

export function StudentDashboardLayout(props: StudentDashboardLayoutProps) {
  return (
    <HeaderGradientProvider>
      <StudentDashboardLayoutInner {...props} />
    </HeaderGradientProvider>
  );
}

StudentDashboardLayout.displayName = 'StudentDashboardLayout';
